import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { Coins, Gift, HandHeart, LineChart, ShieldCheck, Users } from "lucide-react"
import { PostSubscription } from "../PostSubscription"
import { StatusMessage } from "../ui/StatusMessage"
import { useFetchSubscriptions } from "../../hooks/DataFetching/useFetchSubscriptions"
import ChurchConnectCard from "./ChurchConnectCard"
import { bannerImageStyle, parseFraming } from "./bannerFraming"
import { getApiBaseUrl } from "../../config/apiBase"
import { FEATURE_FLAGS, isFeatureEnabled } from "../../config/featureFlags"

const Fact = ({ label, value }) => (
  <div className="tyk-stat">
    <span>{label}</span>
    <strong>{value || "N/A"}</strong>
  </div>
)

const Detail = ({ label, value }) => {
  if (value === null || value === undefined || value === "") {
    return null
  }

  return (
    <div>
      <span className="text-xs font-bold uppercase text-yellow-200/80">{label}</span>
      <p className="mt-1 text-sm leading-6 text-slate-200">{value}</p>
    </div>
  )
}

const GroupPublicPage = () => {
  const bitcoinEnabled = isFeatureEnabled(FEATURE_FLAGS.bitcoinExperience)
  const { groupId } = useParams()
  const [group, setGroup] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [localMembership, setLocalMembership] = useState(null)
  const [removedMembershipIds, setRemovedMembershipIds] = useState([])
  const { getAccessTokenSilently } = useAuth0()
  const { subscriptions, loading: subscriptionsLoading } = useFetchSubscriptions()
  const apiUrl = getApiBaseUrl()

  useEffect(() => {
    const fetchGroupData = async () => {
      setLoading(true)
      setError("")
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
        })

        const response = await fetch(`${apiUrl}/api/GroupsTbls/${groupId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })

        if (!response.ok) {
          throw new Error("Could not load this group.")
        }

        const data = await response.json()
        setGroup(data)
      } catch (err) {
        console.error("Error loading public group:", err)
        setError(err.message || "Could not load this group.")
      } finally {
        setLoading(false)
      }
    }

    fetchGroupData()
  }, [apiUrl, getAccessTokenSilently, groupId])

  const currentMembership = localMembership || subscriptions.find((subscription) => (
    String(subscription.groupId) === String(groupId) &&
    !removedMembershipIds.includes(subscription.id)
  ))

  const handleJoined = (createdMembership) => {
    setLocalMembership(createdMembership || { groupId: group?.id })
    setRemovedMembershipIds((ids) => ids.filter((id) => id !== createdMembership?.id))
    setGroup((previous) => previous
      ? { ...previous, subscriptions: Number(previous.subscriptions || 0) + 1 }
      : previous)
  }

  const handleLeft = (membership) => {
    setLocalMembership(null)
    if (membership?.id) {
      setRemovedMembershipIds((ids) => [...ids, membership.id])
    }
    setGroup((previous) => previous
      ? { ...previous, subscriptions: Math.max(Number(previous.subscriptions || 0) - 1, 0) }
      : previous)
  }

  if (loading || subscriptionsLoading) {
    return (
      <main className="tyk-page">
        <div className="tyk-page-shell">
          <StatusMessage tone="loading" title="Loading group" message="We are loading the latest group profile." />
        </div>
      </main>
    )
  }

  if (error || !group) {
    return (
      <main className="tyk-page">
        <div className="tyk-page-shell">
          <StatusMessage tone="error" title="Could not load group" message={error || "This group is unavailable."} />
        </div>
      </main>
    )
  }

  const banner = group.bannerImage || group.bannerUrl || group.profilePicture
  const thumbnail = group.thumbnailUrl || group.profilePictureUrl
  const isGeographicGroup = (group.groupType || "").toLowerCase() === "geographic" || Boolean(group.geoKey)
  const isCongregation = (group.groupType || "").toLowerCase() === "congregation"
  const isMinistry = (group.groupType || "").toLowerCase() === "ministry"
  const shouldUseChurchConnect = isCongregation && !currentMembership && !isGeographicGroup

  return (
    <main className="tyk-page">
      <div className="tyk-page-shell">
        <section className="tyk-hero-panel overflow-hidden p-0">
          <div className="grid w-full gap-0 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="p-7 md:p-9">
              {thumbnail && (
                <img
                  src={thumbnail}
                  alt={`${group.groupName} thumbnail`}
                  className="mb-5 h-24 w-24 rounded-2xl border border-yellow-200/30 bg-slate-950 object-cover shadow-xl"
                />
              )}
              <span className="tyk-kicker">
                <Users size={16} aria-hidden="true" />
                Public group profile
              </span>
              <h1 className="tyk-hero-title">{group.groupName}</h1>
              <p className="tyk-hero-copy">{group.groupDescription || "This group has not published a description yet."}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                <span className="tyk-pill">{group.groupType || "Community group"}</span>
                <span className="tyk-pill">{group.eligibleTo || "Eligibility not listed"}</span>
              </div>
            </div>
            {banner && (
              <div className="h-72 w-full lg:h-full" style={{ overflow: 'hidden', background: parseFraming(group.bannerFraming).background }}>
              <img
                src={banner}
                alt=""
                className="h-72 w-full object-cover lg:h-full"
                style={bannerImageStyle(group.bannerFraming)}
              />
              </div>
            )}
          </div>
        </section>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="grid gap-5">
            <section className="tyk-panel">
              <span className="tyk-kicker">
                <ShieldCheck size={15} aria-hidden="true" />
                Mission
              </span>
              <p className="mt-3 text-base leading-7 text-slate-200">
                {group.groupMission || "This group has not published a mission statement yet."}
              </p>
            </section>

            <section className="tyk-stat-row">
              <Fact label="Members" value={group.subscriptions || 0} />
              {bitcoinEnabled && <Fact label="Enrollment fee" value={`${group.enrollmentFee || 0} BTC`} />}
              {bitcoinEnabled && <Fact label="Monthly dues" value={`${group.monthlyDues || 0} BTC`} />}
              {bitcoinEnabled && <Fact label="Avg. member profit" value={`${group.monthlyDividends || 0} BTC`} />}
            </section>

            {isCongregation && (
              <section className="tyk-panel">
                <span className="tyk-kicker">Congregation / Ekklesia</span>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <Detail label="Congregation name" value={group.congregationName || group.groupName} />
                  <Detail label="Listing status" value={group.congregationStatus || "Suggested"} />
                  <Detail label="Denomination / tradition" value={group.denominationOrTradition} />
                  <Detail label="Pastor / elder / representative contacts" value={group.clergyContact} />
                  <Detail label="Meeting times" value={group.meetingTimes} />
                  <Detail label="Member count estimate" value={group.memberCountEstimate} />
                  <Detail label="Website" value={group.websiteUrl} />
                  <Detail label="Social links" value={group.socialLinks} />
                  <Detail label="Representative role" value={group.representativeRole} />
                  <Detail label="Authorization notes" value={group.representationAuthorizationNotes} />
                  <Detail label="Ministries offered" value={group.ministriesOffered} />
                  <Detail label="Needs" value={group.needs} />
                  <Detail label="Resources available" value={group.resourcesAvailable} />
                </div>
              </section>
            )}

            {isMinistry && (
              <section className="tyk-panel">
                <span className="tyk-kicker">
                  <HandHeart size={15} aria-hidden="true" />
                  Ministry support
                </span>
                <p className="mt-3 text-base leading-7 text-slate-200">
                  This ministry can gather prayer, volunteers, needs, gifts, and transparent stewardship in one shareable place.
                </p>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <Detail label="Ministry focus" value={group.ministriesOffered || group.groupCategory} />
                  <Detail label="Current needs" value={group.needs} />
                  <Detail label="Resources available" value={group.resourcesAvailable} />
                  <Detail label="Website" value={group.websiteUrl} />
                  <Detail label="Social links" value={group.socialLinks} />
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <a className="tyk-button tyk-button-primary" href={`/ministries/${group.id}/support`}>
                    <Gift size={16} aria-hidden="true" />
                    Open support page
                  </a>
                  <a className="tyk-button" href={`/needs?groupId=${group.id}`}>
                    See ministry needs
                  </a>
                </div>
              </section>
            )}

            <section className="tyk-grid">
              <article className="tyk-panel">
                <span className="tyk-kicker">
                  <LineChart size={15} aria-hidden="true" />
                  Performance
                </span>
                <div className="mt-4 grid gap-3 text-sm text-slate-300">
                  <p>Actions executed: <strong className="text-slate-50">{group.totalActions || "N/A"}</strong></p>
                  <p>Successful actions: <strong className="text-slate-50">{group.successfulActions || "N/A"}</strong></p>
                  <p>Success rate: <strong className="text-slate-50">{group.successRate ? `${group.successRate}%` : "N/A"}</strong></p>
                </div>
              </article>
              {bitcoinEnabled && <article className="tyk-panel">
                <span className="tyk-kicker">
                  <Coins size={15} aria-hidden="true" />
                  Financial flow
                </span>
                <div className="mt-4 grid gap-3 text-sm text-slate-300">
                  <p>Funds to actions: <strong className="text-slate-50">{group.fundsToActions || "N/A"} BTC</strong></p>
                  <p>Funds paid to members: <strong className="text-slate-50">{group.fundsToMembers || "N/A"} BTC</strong></p>
                </div>
              </article>}
            </section>
          </div>

          <aside className="grid gap-5">
            <section className="tyk-panel border-yellow-300/40">
              <span className="tyk-kicker">
                {isGeographicGroup ? "Profile-managed location" : currentMembership ? "Membership" : "Join this group"}
              </span>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                {isGeographicGroup
                  ? "This geographic group follows your profile address. Change your address in your profile or dashboard to update your location groups."
                  : currentMembership
                  ? "Your membership is active. Continue into the member workspace or leave when needed."
                  : "Review eligibility and join when you are ready to participate."}
              </p>
              <div className="mt-5">
                {shouldUseChurchConnect ? (
                  <ChurchConnectCard
                    churchRecord={{
                      id: group.id,
                      groupName: group.congregationName || group.groupName,
                      eligibleTo: group.eligibleTo,
                    }}
                    onConnected={(result) => {
                      if (result?.status === "Joined" || result?.status === "AlreadyMember") {
                        handleJoined({ id: result.subscriptionId, groupId: group.id })
                      }
                    }}
                  />
                ) : (
                  <PostSubscription
                    groupId={group.id}
                    eligibleTo={group.eligibleTo}
                    membership={currentMembership}
                    onJoined={handleJoined}
                    onLeft={handleLeft}
                    isProfileManaged={isGeographicGroup}
                  />
                )}
              </div>
            </section>
            <section className="tyk-panel">
              <span className="tyk-kicker">Policies</span>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                {group.policies || "This group has not published any policies yet."}
              </p>
            </section>
          </aside>
        </div>
      </div>
    </main>
  )
}

export default GroupPublicPage
